const buildAuthHeaders = (token, extraHeaders = {}) => ({
  ...((token || '').trim() ? { Authorization: `Bearer ${token}` } : {}),
  ...extraHeaders,
});

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);
const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 404, 405, 409, 410, 422]);

const RETRY_PROFILES = {
  read: {
    maxAttempts: 3,
    baseDelayMs: 300,
    maxDelayMs: 4000,
    jitterRatio: 0.25,
  },
  write: {
    maxAttempts: 2,
    baseDelayMs: 400,
    maxDelayMs: 5000,
    jitterRatio: 0.2,
  },
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseRetryAfterMs = (response) => {
  const retryAfter = response?.headers?.get('retry-after');
  if (!retryAfter) return null;

  const asNumber = Number(retryAfter);
  if (Number.isFinite(asNumber)) {
    return Math.max(0, asNumber * 1000);
  }

  const asDate = Date.parse(retryAfter);
  if (!Number.isNaN(asDate)) {
    return Math.max(0, asDate - Date.now());
  }

  return null;
};

const computeBackoffDelayMs = (attempt, retryProfile, retryAfterMs = null) => {
  if (retryAfterMs !== null && Number.isFinite(retryAfterMs)) {
    return Math.min(retryProfile.maxDelayMs, retryAfterMs);
  }

  const exponentialDelay = Math.min(
    retryProfile.maxDelayMs,
    retryProfile.baseDelayMs * 2 ** Math.max(0, attempt - 1)
  );
  const jitterWindow = exponentialDelay * retryProfile.jitterRatio;
  const jitter = (Math.random() * 2 - 1) * jitterWindow;
  return Math.max(0, Math.round(exponentialDelay + jitter));
};

const shouldRetryResponse = (response, attempt, retryProfile) => {
  if (response.ok) return false;
  if (attempt >= retryProfile.maxAttempts) return false;
  if (NON_RETRYABLE_STATUS_CODES.has(response.status)) return false;
  return RETRYABLE_STATUS_CODES.has(response.status);
};

const shouldRetryError = (error, attempt, retryProfile) => {
  if (attempt >= retryProfile.maxAttempts) return false;
  if (!error) return false;
  if (error.name === 'AbortError') return false;
  return true;
};

const parseResponsePayload = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const rawText = await response.text();
  if (!rawText) return {};

  try {
    return JSON.parse(rawText);
  } catch (_) {
    return rawText;
  }
};

const fetchWithSmartRetry = async (requestConfig, retryProfileKey = 'read') => {
  const retryProfile = RETRY_PROFILES[retryProfileKey] || RETRY_PROFILES.read;
  let attempt = 1;

  while (attempt <= retryProfile.maxAttempts) {
    try {
      const response = await fetch(requestConfig.url, {
        method: requestConfig.method,
        headers: requestConfig.headers,
        body: requestConfig.body,
        credentials: requestConfig.credentials,
      });

      if (!shouldRetryResponse(response, attempt, retryProfile)) {
        return response;
      }

      const retryAfterMs = parseRetryAfterMs(response);
      const delayMs = computeBackoffDelayMs(attempt, retryProfile, retryAfterMs);
      await sleep(delayMs);
      attempt += 1;
    } catch (error) {
      if (!shouldRetryError(error, attempt, retryProfile)) {
        throw error;
      }

      const delayMs = computeBackoffDelayMs(attempt, retryProfile);
      await sleep(delayMs);
      attempt += 1;
    }
  }

  throw new Error('Request failed after retries');
};

const executeApiRequest = async (requestConfig, retryProfileKey = 'read') => {
  try {
    const response = await fetchWithSmartRetry(requestConfig, retryProfileKey);
    return parseResponsePayload(response);
  } catch (error) {
    console.error('API request failed:', error);
    return {
      error: 'network_request_failed',
      message: error?.message || 'Network request failed',
    };
  }
};

export const fetchEmailData = (url, data) => {
  return executeApiRequest(
    {
      url,
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Accept:
          'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
      },
    },
    'write'
  );
};

export const fetchApiDataWithTargetUserAndClientId = (url, token, actionType, accessLevel, userId, clientId) => {
  const headers = buildAuthHeaders(token, {
    targetUser: userId,
    id: clientId,
    clientId: clientId,
    ...(actionType && { Action: actionType }),
    'Content-Type': 'application/json',
  });
  return executeApiRequest(
    {
      url,
      method: 'GET',
      headers: headers,
    },
    'read'
  );
};

export const fetchApiData = (url, token, actionType, accessLevel, clientId) => {
  const headers = buildAuthHeaders(token, {
    id: clientId,
    ...(actionType && { Action: actionType }),
    ...(accessLevel === 'AccountAdmin' && { id: clientId }),
    'Content-Type': 'application/json',
  });
  return executeApiRequest(
    {
      url,
      method: 'GET',
      headers: headers,
    },
    'read'
  );
};

export const fetchApiDataWithUserAndClientId = (url, token, actionType, accessLevel, userId, clientId) => {
  const headers = buildAuthHeaders(token, {
    id: userId,
    clientId: clientId,
    ...(actionType && { Action: actionType }),
    'Content-Type': 'application/json',
  });
  return executeApiRequest(
    {
      url,
      method: 'GET',
      headers: headers,
    },
    'read'
  );
};

export const fetchOldApiData = (url, token) => {
  return executeApiRequest(
    {
      url,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
    'read'
  );
};

export const fetchLinkAnalysisData = (url, token, actionType, finalUrl) => {
  console.log('service', finalUrl);
  return executeApiRequest(
    {
      url,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(actionType && { Action: actionType }),
        ...(finalUrl && { link: finalUrl }),
        'Content-Type': 'application/json',
      },
    },
    'read'
  );
};

export const postApiData = (url, token, data, actionType) => {
  const headers = buildAuthHeaders(token, {
    ...(actionType && { Action: actionType }),
    'Content-Type': 'application/json',
  });
  return executeApiRequest(
    {
      url,
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify(data),
      headers: headers,
    },
    'write'
  );
};

export const postApiDataNoToken = (url, data, actionType) => {
  return executeApiRequest(
    {
      url,
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify(data),
      headers: {
        ...(actionType && { Action: actionType }),
        'Content-Type': 'application/json',
      },
    },
    'write'
  );
};
